import { RefObject, useCallback, useEffect, useRef } from 'react'
import { FlatList, NativeScrollEvent, NativeSyntheticEvent } from 'react-native'

const NEAR_END_THRESHOLD_PX = 120
const SCROLL_DOWN_MIN_DELTA_PX = 4

type ScrollMetrics = {
  offsetY: number
  layoutHeight: number
  contentHeight: number
}

type Options = {
  /** When false, pagination is not allowed (e.g. no more pages, initial load). */
  enabled: boolean
  /** True while a page request is in flight — blocks duplicate calls. */
  isLoading: boolean
  listRef: RefObject<FlatList<unknown> | null>
  resetKey?: string
}

/**
 * Paginated FlatList helper:
 * - One in-flight request at a time
 * - Loads only after the user scrolls toward the end (not on content-size auto-chain)
 * - Restores scroll offset after append so new rows sit below the viewport
 */
export function useLoadMoreOnEndReached(
  loadMore: () => Promise<unknown>,
  options: Options,
) {
  const { enabled, isLoading, listRef, resetKey } = options

  const enabledRef = useRef(enabled)
  const isLoadingRef = useRef(isLoading)
  const inFlightRef = useRef(false)
  const userHasScrolledRef = useRef(false)
  const previousOffsetRef = useRef(0)
  const scrollMetricsRef = useRef<ScrollMetrics>({
    offsetY: 0,
    layoutHeight: 0,
    contentHeight: 0,
  })

  enabledRef.current = enabled
  isLoadingRef.current = isLoading

  useEffect(() => {
    userHasScrolledRef.current = false
    inFlightRef.current = false
    previousOffsetRef.current = 0
    scrollMetricsRef.current = { offsetY: 0, layoutHeight: 0, contentHeight: 0 }
  }, [resetKey])

  const getDistanceFromEnd = useCallback(() => {
    const { contentHeight, layoutHeight, offsetY } = scrollMetricsRef.current
    if (!contentHeight || !layoutHeight) return Number.POSITIVE_INFINITY
    return contentHeight - layoutHeight - offsetY
  }, [])

  const isNearEnd = useCallback(() => {
    return getDistanceFromEnd() <= NEAR_END_THRESHOLD_PX
  }, [getDistanceFromEnd])

  const pendingScrollRestoreRef = useRef<number | null>(null)

  const restoreScrollOffset = useCallback((offsetY: number) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        listRef.current?.scrollToOffset({ offset: offsetY, animated: false })
        scrollMetricsRef.current.offsetY = offsetY
        previousOffsetRef.current = offsetY
      })
    })
  }, [listRef])

  useEffect(() => {
    if (isLoading || pendingScrollRestoreRef.current === null) return
    const offsetY = pendingScrollRestoreRef.current
    pendingScrollRestoreRef.current = null
    restoreScrollOffset(offsetY)
  }, [isLoading, restoreScrollOffset])

  const tryLoadMore = useCallback(async () => {
    if (!enabledRef.current || isLoadingRef.current || inFlightRef.current) return
    if (!userHasScrolledRef.current) return

    pendingScrollRestoreRef.current = scrollMetricsRef.current.offsetY
    inFlightRef.current = true

    try {
      await loadMore()
    } finally {
      inFlightRef.current = false
    }
  }, [loadMore])

  const updateScrollMetrics = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent
    scrollMetricsRef.current = {
      offsetY: contentOffset.y,
      layoutHeight: layoutMeasurement.height,
      contentHeight: contentSize.height,
    }
    if (contentOffset.y > 8) {
      userHasScrolledRef.current = true
    }
  }, [])

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offsetY = event.nativeEvent.contentOffset.y
      const scrollingDown = offsetY > previousOffsetRef.current + SCROLL_DOWN_MIN_DELTA_PX
      previousOffsetRef.current = offsetY
      updateScrollMetrics(event)

      if (scrollingDown && isNearEnd()) {
        void tryLoadMore()
      }
    },
    [isNearEnd, tryLoadMore, updateScrollMetrics],
  )

  const onMomentumScrollBegin = useCallback(() => {
    userHasScrolledRef.current = true
  }, [])

  const onEndReached = useCallback(() => {
    if (!userHasScrolledRef.current) return
    void tryLoadMore()
  }, [tryLoadMore])

  return {
    onEndReached,
    onMomentumScrollBegin,
    onScroll,
  }
}
